// MV3 / R-008: architecture proof for doorway routes and wall margins. Reuse the destination resolver's floor-band, exterior-ground,
// headroom and body-clearance rules at every sampled foot. Live doors cut passable openings out of solid faces; a closed door blocks.
// Unknown overlays stay unproven. Props and bodies belong to the scope's separate occupancy gates.
import { liftDestinationClear, liftDestinationWallDepth, LiftUncertain, floorBandShape, withExteriorOpenGround } from '../inventory/lift-occupancy.js';
import { segmentHit } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { squeezeBodyFt } from '../../../../ft3d/engine/ft3d-creature-size.js';
import { physicalBodyNavigationShape } from '../../shared/physical-creature-body.js';
import type { RestShape, Zone } from '../inventory/floor-move.js';
import type { WalkSupportSurface } from './traversal-3d';
import { movementFootprint } from './body-geometry.js';

export interface RouteWallWorld {
  /** ft_encounter_shape(): the scene's overlaid shape (live door states), or the authored one with `overlayUnknown`. */
  shape: RestShape | null;
  /** ft_encounter_canvas_is_exterior(): encounter_inside !== 1. */
  canvasExterior: boolean;
  overlayUnknown: boolean;
}
export interface RouteWallBody { diameter: number; height: number; widthFt?: number; baseFt: number; mesh: boolean; allowSqueeze?: boolean; squeezeSizeFt?: number }
type WallSlab = {
  axis: 'x' | 'y'; at: number; span: { min: number; max: number }; z: { min: number; max: number };
  openings?: Array<{ span: { min: number; max: number }; z?: { min: number; max: number } }>;
};
export type RouteWallVerdict = true | false | 'unknown';
/** A per-foot wall verdict for one mover starting at `persisted` (its feet before the walk), or null when the page cannot judge at all. */
export type RouteWallReader = ((x: number, y: number, z: number) => RouteWallVerdict) & {
  /** Supported room steps prove floor/headroom separately, across multiple floor bands. */
  structural?: (floors: readonly WalkSupportSurface[], route?: ReadonlyArray<readonly number[]>) => RouteWallReader;
  /** Same architecture and overlays as the boolean proof; unknown or unmeasured obstructions grant no escape. */
  penetrationDepth?: (x: number, y: number, z: number) => number | 'unknown';
  /** Whether the centre segment crosses a solid face or a scene-wall slab mid-line. */
  crosses?: (ax: number, ay: number, bx: number, by: number, z: number) => boolean;
};

export function routeWallReader(world: RouteWallWorld | null, body: RouteWallBody, persisted: { x: number; y: number; z: number }): RouteWallReader | null {
  if (!world?.shape || !Array.isArray(world.shape.boxes)) return null;
  if (![body.diameter, body.height, body.baseFt].every(v => Number.isFinite(v) && v > 0)) return null;
  // Callers check intervening centre segments as well as per-foot clearance, including Tiny bodies.
  if (![persisted.x, persisted.y, persisted.z].every(Number.isFinite)) return null;
  const shape = body.mesh ? physicalBodyNavigationShape(world.shape as never, body.height) as unknown as RestShape : world.shape;
  const allowSqueeze = body.allowSqueeze ?? !body.mesh;
  const footprintAt = (px: number, py: number, pz: number): Zone => movementFootprint(body, px, py, pz);
  const destination = { shape, canvasExterior: world.canvasExterior, persisted: { ...persisted }, footprintAt, heightFt: body.height,
    tierFt: body.baseFt, obstacles: () => [] as Zone[], overlayUnknown: world.overlayUnknown, allowSqueeze, squeezeSizeFt: body.squeezeSizeFt };
  const reader: RouteWallReader = (x, y, z) => {
    if (![x, y, z].every(Number.isFinite)) return false;
    try { return liftDestinationClear(destination, x, y, z); } catch (error) {
      if (error instanceof LiftUncertain) return 'unknown';
      throw error;
    }
  };
  reader.penetrationDepth = (x, y, z) => {
    if (![x, y, z].every(Number.isFinite)) return 'unknown';
    try { return liftDestinationWallDepth(destination, x, y, z) ?? 'unknown'; } catch (error) {
      if (error instanceof LiftUncertain) return 'unknown';
      throw error;
    }
  };
  reader.crosses = (ax, ay, bx, by, z) => {
    const band = floorBandShape(shape, persisted.x, persisted.y, z, world.canvasExterior);
    if (!band) return true;
    const movement = withExteriorOpenGround(band.shape, ax, ay, bx, by, 40 + body.baseFt, world.canvasExterior);
    const rayZ = z + body.height / 2;
    if (segmentHit({ ...movement, sceneWalls: [] }, ax, ay, bx, by, { z: rayZ, mode: 'move' }) !== null) return true;
    for (const w of (movement.sceneWalls ?? []) as WallSlab[]) {
      if (!w || rayZ < w.z.min || rayZ > w.z.max) continue;
      const na = (w.axis === 'x' ? ax : ay) - w.at, nb = (w.axis === 'x' ? bx : by) - w.at;
      if (!(na * nb < 0)) continue;
      const t = na / (na - nb), along = w.axis === 'x' ? ay + (by - ay) * t : ax + (bx - ax) * t;
      if (along < w.span.min || along > w.span.max) continue;
      if ((w.openings ?? []).some(o => along > o.span.min && along < o.span.max && rayZ >= (o.z?.min ?? -Infinity) && rayZ <= (o.z?.max ?? Infinity))) continue;
      return true;
    }
    return false;
  };
  // Keep every authored floor and cross-band aperture. The ordinary placement
  // probe clamps to the persisted floor and drops other bands, which creates
  // artificial walls at terraces and refuses a legitimate descent.
  const structuralOpenings = (shape.openings ?? []) as Array<{
    plane: { axis: 'x' | 'y'; at: number }; span: { min: number; max: number }; z: { min: number; max: number };
  }>;
  reader.structural = (floors, route) => {
    const radius = allowSqueeze ? squeezeBodyFt(body.squeezeSizeFt ?? body.diameter, 2) / 2 : body.diameter / 2;
    const points = route?.length ? route : floors.flatMap(f => [
      [f.footprint.minX,f.footprint.minY,f.z], [f.footprint.maxX,f.footprint.maxY,f.z],
    ]);
    const minX = Math.min(...points.map(p=>p[0]!))-radius, maxX = Math.max(...points.map(p=>p[0]!))+radius;
    const minY = Math.min(...points.map(p=>p[1]!))-radius, maxY = Math.max(...points.map(p=>p[1]!))+radius;
    const feet = [...floors.map(f=>f.z), ...points.map(p=>p[2]).filter((z): z is number => Number.isFinite(z))];
    const minZ = Math.min(persisted.z,...feet), maxZ = Math.max(persisted.z,...feet)+body.height;
    const lowestFloor = Math.min(...floors.map(f => f.z));
    const nearby = shape.boxes.filter(box => box.x.max >= minX && box.x.min <= maxX
      && box.y.max >= minY && box.y.min <= maxY && box.ceiling > minZ && box.floor < maxZ);
    // Raised floor foundations are intended risers; explicit scene walls stay.
    const rooms = nearby.filter(box => {
      const c = box.creation;
      return !box.navigationOpenGround && c?.enclosure !== 'none' && c?.environment !== 'exterior-open' && c?.presetId !== 'exterior-open'
        && !floors.some(f => f.z > lowestFloor + 1e-6 && Math.abs(box.ceiling-f.z) <= 1e-6 && box.floor < box.ceiling
          && box.x.min >= f.footprint.minX-1e-6 && box.x.max <= f.footprint.maxX+1e-6
          && box.y.min >= f.footprint.minY-1e-6 && box.y.max <= f.footprint.maxY+1e-6);
    });
    type Piece = { axis: 'x' | 'y'; at: number; half: number; lo: number; hi: number; bottom: number; top: number };
    const pieces = new Map<string,Piece>();
    const near = (p: Piece): boolean => p.top > minZ && p.bottom < maxZ && (p.axis === 'x'
      ? p.at+p.half >= minX && p.at-p.half <= maxX && p.hi >= minY && p.lo <= maxY
      : p.at+p.half >= minY && p.at-p.half <= maxY && p.hi >= minX && p.lo <= maxX);
    const subtract = (parts: Piece[], lo: number, hi: number, bottom: number, top: number): Piece[] => parts.flatMap(p => {
      const a=Math.max(p.lo,lo), b=Math.min(p.hi,hi), c=Math.max(p.bottom,bottom), d=Math.min(p.top,top);
      if (b-a <= 1e-6 || d-c <= 1e-6) return [p];
      return [{...p,hi:a},{...p,lo:b},{...p,lo:a,hi:b,top:c},{...p,lo:a,hi:b,bottom:d}]
        .filter(p=>p.hi-p.lo>1e-6&&p.top-p.bottom>1e-6);
    });
    const retain = (parts: Piece[]) => {
      for (const p of parts) if (near(p)) pieces.set([p.axis,p.at,p.half,p.lo,p.hi,p.bottom,p.top].join(':'),p);
    };
    // Resolve solid pieces once, including overlap and aperture cuts. Filter
    // faces and possible room pairs before comparing; samples never read the
    // full structure once for every wall end.
    for (const box of rooms) for (const axis of ['x','y'] as const) {
      const along=axis==='x'?'y':'x';
      for (const at of [box[axis].min,box[axis].max]) {
        const face: Piece={axis,at,half:0,lo:box[along].min,hi:box[along].max,bottom:box.floor,top:box.ceiling};
        if (!near(face)) continue;
        let parts=[face];
        for (const other of rooms) {
          if (at <= other[axis].min+1e-6 || at >= other[axis].max-1e-6
            || other[along].max <= face.lo || other[along].min >= face.hi || other.ceiling <= face.bottom || other.floor >= face.top) continue;
          parts=subtract(parts,other[along].min,other[along].max,other.floor,other.ceiling);
        }
        for (const o of structuralOpenings) if ((o as {passable?: boolean}).passable !== false
          && o.plane.axis===axis && Math.abs(o.plane.at-at)<=1e-6) parts=subtract(parts,o.span.min,o.span.max,o.z.min,o.z.max);
        retain(parts);
      }
    }
    for (const wall of (shape.sceneWalls ?? []) as Array<WallSlab & { thicknessFt?: number }>) {
      const face: Piece={axis:wall.axis,at:wall.at,half:Math.max(.05,wall.thicknessFt??.5)/2,
        lo:wall.span.min,hi:wall.span.max,bottom:wall.z.min,top:wall.z.max};
      if (!near(face)) continue;
      let parts=[face];
      for (const o of wall.openings ?? []) parts=subtract(parts,o.span.min,o.span.max,o.z?.min??-Infinity,o.z?.max??Infinity);
      retain(parts);
    }
    const solids=[...pieces.values()];
    const depth = (p: Piece, x: number, y: number, signed: boolean): number => {
      const normal=(p.axis==='x'?x:y)-p.at, along=p.axis==='x'?y:x;
      const normalGap=Math.abs(normal)-p.half, alongGap=Math.max(p.lo-along,along-p.hi);
      let distance=Math.hypot(Math.max(0,normalGap),Math.max(0,alongGap))+Math.min(0,Math.max(normalGap,alongGap));
      const originNormal=(p.axis==='x'?persisted.x:persisted.y)-p.at;
      if (signed && Math.abs(originNormal)>1e-6 && originNormal*normal<0 && along>=p.lo && along<=p.hi)
        distance=-Math.abs(normal)-p.half;
      return Math.max(0,radius-distance);
    };
    const inHeight = (p: Piece,z: number): boolean => p.top > z+1e-6 && p.bottom < z+body.height-1e-6;
    const structural: RouteWallReader = (x,y,z) => {
      if (![x,y,z].every(Number.isFinite)) return false;
      if (world.overlayUnknown) return 'unknown';
      return solids.every(p=>!inHeight(p,z)||depth(p,x,y,false)<=1e-6);
    };
    structural.penetrationDepth = (x,y,z) => world.overlayUnknown ? 'unknown'
      : Math.max(0,...solids.filter(p=>inHeight(p,z)).map(p=>depth(p,x,y,true)));
    structural.crosses = (ax,ay,bx,by,z) => {
      const dx=bx-ax, dy=by-ay, length2=dx*dx+dy*dy;
      if (length2<=1e-12) return false;
      return solids.some(p => {
        if (!inHeight(p,z)) return false;
        const normalA=p.axis==='x'?ax:ay, alongA=p.axis==='x'?ay:ax;
        const dn=p.axis==='x'?dx:dy, da=p.axis==='x'?dy:dx;
        if (Math.max(normalA,normalA+dn)+radius < p.at-p.half || Math.min(normalA,normalA+dn)-radius > p.at+p.half
          || Math.max(alongA,alongA+da)+radius < p.lo || Math.min(alongA,alongA+da)-radius > p.hi) return false;
        const ts=new Set([0,1]);
        for (const n of [p.at-p.half,p.at,p.at+p.half]) {
          if (Math.abs(dn)>1e-12) ts.add(Math.max(0,Math.min(1,(n-normalA)/dn)));
          for (const a of [p.lo,p.hi]) ts.add(Math.max(0,Math.min(1,((n-normalA)*dn+(a-alongA)*da)/length2)));
        }
        for (const a of [p.lo,p.hi]) if (Math.abs(da)>1e-12) ts.add(Math.max(0,Math.min(1,(a-alongA)/da)));
        const start=depth(p,ax,ay,false);
        const initial=inHeight(p,persisted.z)?depth(p,persisted.x,persisted.y,false):0;
        const escaping=initial>1e-6 && start>1e-6;
        const maximum=Math.max(...[...ts].map(t=>depth(p,ax+t*dx,ay+t*dy,escaping)));
        if (maximum<=1e-6) return false;
        // Match wallClearLeavingOverlap: only a pre-existing penetration may
        // shrink until the first clear position. Crossing its face deepens it.
        return !(escaping && start<=initial+1e-6 && maximum<=start+1e-6);
      });
    };
    return structural;
  };
  return reader;
}
